
// Common and trusted TLDs
const COMMON_TLDS = [
  'com', 'org', 'net', 'edu', 'gov', 'mil', 'int',
  'co.uk', 'org.uk', 'ac.uk', 'gov.uk',
  'com.au', 'org.au', 'net.au', 'edu.au', 'gov.au',
  'ca', 'de', 'fr', 'it', 'es', 'nl', 'se', 'no', 'dk', 'fi',
  'jp', 'kr', 'cn', 'in', 'br', 'mx', 'ru'
];

// Suspicious or commonly mistyped TLDs
const SUSPICIOUS_TLDS = [
  'co', 'cm', 'con', 'tk', 'ml', 'ga', 'cf'
];

// Common email domain suggestions
const DOMAIN_SUGGESTIONS = {
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.con': 'gmail.com',
  'hotmail.co': 'hotmail.com',
  'hotmail.cm': 'hotmail.com',
  'yahoo.co': 'yahoo.com',
  'yahoo.cm': 'yahoo.com',
  'outlook.co': 'outlook.com',
  'outlook.cm': 'outlook.com'
};

export interface EmailValidationResult {
  isValid: boolean;
  warning?: string;
  suggestion?: string;
  type?: 'suspicious' | 'uncommon' | 'suggestion';
}

export function validateEmailTLD(email: string): EmailValidationResult {
  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailRegex.test(email)) {
    return { isValid: false };
  }

  const [, domain] = email.split('@');
  if (!domain) {
    return { isValid: false };
  }

  const domainLower = domain.toLowerCase();
  
  // Check for direct domain suggestions
  if (DOMAIN_SUGGESTIONS[domainLower]) {
    return {
      isValid: true,
      warning: `Did you mean ${DOMAIN_SUGGESTIONS[domainLower]}?`,
      suggestion: email.replace(domain, DOMAIN_SUGGESTIONS[domainLower]),
      type: 'suggestion'
    };
  }

  // Extract TLD
  const tldMatch = domain.match(/\.([a-z]{2,})$/i);
  if (!tldMatch) {
    return { isValid: false };
  }

  const tld = tldMatch[1].toLowerCase();
  
  // Check for suspicious TLDs
  if (SUSPICIOUS_TLDS.includes(tld)) {
    return {
      isValid: true,
      warning: `The domain "${domain}" uses an uncommon extension (.${tld}). Please double-check your email address.`,
      type: 'suspicious'
    };
  }

  // Check for uncommon TLDs
  if (!COMMON_TLDS.includes(tld) && !COMMON_TLDS.some(commonTld => domain.endsWith(commonTld))) {
    return {
      isValid: true,
      warning: `The domain "${domain}" uses an uncommon extension (.${tld}). Please verify this is correct.`,
      type: 'uncommon'
    };
  }

  return { isValid: true };
}
